import { collection, doc, getDocs, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config';
import type { CreateSubPatternRequest } from '../../../../types/subPattern';
import { convertItemOverridesToSnakeCase } from '../../../../utils/pattern/subPatternConverter';
import { syncHasSubPatterns } from './syncHasSubPatterns';

/**
 * 子パターンを1件作成する（#2967）。
 *
 * 表示順は既存子パターンの最大値 + 1 とし、新しく作ったものが末尾に並ぶようにする。
 * 読取パターン本体（display_order を全件書き換えて先頭に挿入）とは方針が異なるが、
 * 子パターンは「あとから足していく例外対応」の性格が強く、追加順に並ぶほうが分かりやすい。
 */
export const createSubPattern = async (request: CreateSubPatternRequest): Promise<string> => {
  const subPatternsRef = collection(db, 'patterns', request.patternId, 'sub_patterns');

  const existing = await getDocs(subPatternsRef);

  const maxOrder = existing.docs.reduce((max, d) => {
    const order = (d.data()?.display_order as number | undefined) ?? 0;
    return order > max ? order : max;
  }, -1);

  const newDocRef = doc(subPatternsRef);
  const now = Timestamp.now();

  await setDoc(newDocRef, {
    sub_pattern_name: request.subPatternName,
    description: request.description,
    item_overrides: convertItemOverridesToSnakeCase(request.itemOverrides),
    is_active: true,
    display_order: maxOrder + 1,
    source_result_id: request.sourceResultId ?? null,
    created_at: now,
    updated_at: now,
    created_by: request.userId,
    updated_by: request.userId,
    // 代理ログイン中は created_by に運営スタッフのUIDが入る。
    // 一覧で作成者を「事務局」と出せるよう、読取パターンと同じく代理フラグを残す（#3314）
    ...(request.isProxy !== undefined && { is_proxy: request.isProxy }),
    ...(request.operatorId && { operator_id: request.operatorId }),
  });

  // 親の判定フラグを更新（子パターン判定を実行するかの判定に使う）
  await syncHasSubPatterns(request.patternId);

  return newDocRef.id;
};
